import type { z } from 'zod';
import { tokenStore } from '@/auth/token-store';
import { API_BASE_URL, STRICT_SCHEMAS } from './config';
import { ApiError, ErrorCode, type ProblemDetails } from './errors';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Readonly<Record<string, QueryValue>>;

export interface RequestOptions {
  method?: HttpMethod;
  query?: QueryParams | undefined;
  body?: unknown;
  /** Sent as Idempotency-Key. Generate one per user action; reuse it only to retry that same submission. */
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
  /** Attach the bearer token (default true). */
  auth?: boolean;
  /** Optimistic concurrency: sent as `If-Match: "v<version>"`; a stale version answers 412. */
  ifMatch?: number | undefined;
  /** Return a successful body as text instead of parsing JSON (file downloads). */
  raw?: boolean;
  /** Return a successful body as a Blob (PDFs and other binary files). */
  binary?: boolean;
  /**
   * A bearer token other than the staff session's (the customer portal).
   * A 401 for it is the caller's to handle; it never ends the staff session.
   */
  token?: string | undefined;
}

export interface ApiResponse {
  status: number;
  body: unknown;
  requestId: string | null;
  /** The API replayed a stored response for this Idempotency-Key. */
  replayed: boolean;
}

type UnauthorizedHandler = (error: ApiError) => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Registered by the session layer: clears the session and routes to login. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/**
 * Serialize query params the way Laravel reads them: arrays as `key[]=a&key[]=b`,
 * booleans as 1/0 (the `boolean` rule rejects "true"/"false"), empties dropped.
 */
export function buildQuery(query: QueryParams | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(`${key}[]`, String(item));
    } else if (typeof value === 'boolean') {
      params.append(key, value ? '1' : '0');
    } else {
      params.append(key, String(value));
    }
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function buildUrl(path: string, query?: QueryParams): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalized}${buildQuery(query)}`;
}

function isProblem(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).status === 'number' &&
    typeof (value as Record<string, unknown>).code === 'string'
  );
}

function toFieldErrors(value: unknown): Record<string, string[]> {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(messages)) out[field] = messages.filter((m): m is string => typeof m === 'string');
    else if (typeof messages === 'string') out[field] = [messages];
  }
  return out;
}

async function readBody(response: Response, raw = false, binary = false): Promise<unknown> {
  if (response.status === 204) return undefined;
  if (binary && response.ok) return response.blob();
  const text = await response.text();
  if (raw && response.ok) return text;
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Low-level request: returns the parsed body or throws ApiError (problem details). */
export async function apiFetch(path: string, options: RequestOptions = {}): Promise<ApiResponse> {
  const method = options.method ?? 'GET';
  const auth = options.auth ?? true;
  const tokenAtSend = options.token === undefined && auth ? tokenStore.token() : null;
  const bearer = options.token ?? tokenAtSend;
  const headers: Record<string, string> = {
    Accept: options.binary ? 'application/pdf, application/json' : 'application/json',
  };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.ifMatch !== undefined) headers['If-Match'] = `"v${options.ifMatch}"`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: ErrorCode.NetworkError,
      title: 'Network error',
      detail: `Could not reach ${API_BASE_URL}. Check your connection or that the API is running.`,
      method,
      path,
      cause,
    });
  }

  const body = await readBody(response, options.raw ?? false, options.binary ?? false);
  const requestId = response.headers.get('X-Request-Id');

  if (!response.ok) {
    const problem = isProblem(body) ? body : null;
    const error = new ApiError({
      status: response.status,
      code: problem?.code ?? (response.status === 404 ? ErrorCode.RouteNotFound : ErrorCode.InternalError),
      title: problem?.title ?? (response.statusText || 'Request failed'),
      detail: problem?.detail ?? `The API answered ${response.status}.`,
      requestId: problem?.request_id ?? requestId,
      fieldErrors: toFieldErrors(problem?.errors),
      problem,
      method,
      path,
    });

    // A 401 for the credential we still hold ends the session. A 401 for a
    // token that was already replaced (environment/tenant switch revokes the
    // old one) is just a stale in-flight request.
    if (response.status === 401 && tokenAtSend !== null && tokenAtSend === tokenStore.token()) {
      onUnauthorized?.(error);
    }
    throw error;
  }

  return {
    status: response.status,
    body,
    requestId,
    replayed: response.headers.get('Idempotent-Replayed') === 'true',
  };
}

export class SchemaMismatchError extends ApiError {
  readonly issues: z.core.$ZodIssue[];

  constructor(method: string, path: string, issues: z.core.$ZodIssue[]) {
    const first = issues[0];
    const where = first ? `${first.path.join('.') || '(root)'}: ${first.message}` : 'unknown';
    super({
      status: 200,
      code: ErrorCode.InvalidResponse,
      title: 'Unexpected response',
      detail: `${method} ${path} returned a shape the console does not understand (${where}).`,
      method,
      path,
    });
    this.issues = issues;
  }
}

/**
 * Validate a response body. In strict mode drift throws; otherwise it is
 * reported once per endpoint and the raw body is used, so a field the API
 * renamed degrades one cell instead of the whole page.
 */
const reportedDrift = new Set<string>();

export function parseResponse<S extends z.ZodType>(
  schema: S,
  body: unknown,
  method: string,
  path: string,
): z.output<S> {
  const result = schema.safeParse(body);
  if (result.success) return result.data;
  if (STRICT_SCHEMAS) throw new SchemaMismatchError(method, path, result.error.issues);
  const key = `${method} ${path.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id')}`;
  if (!reportedDrift.has(key)) {
    reportedDrift.add(key);
    console.warn(`[biller-api] ${key} response did not match its schema`, result.error.issues);
  }
  return body as z.output<S>;
}

export async function apiRequest<S extends z.ZodType>(
  schema: S,
  path: string,
  options: RequestOptions = {},
): Promise<z.output<S>> {
  const response = await apiFetch(path, options);
  return parseResponse(schema, response.body, options.method ?? 'GET', path);
}

/** A binary download (a PDF), as a Blob. */
export async function apiBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await apiFetch(path, { ...options, binary: true });
  if (!(response.body instanceof Blob)) throw new SchemaMismatchError(options.method ?? 'GET', path, []);
  return response.body;
}

/** For endpoints answering 204 No Content. */
export async function apiVoid(path: string, options: RequestOptions = {}): Promise<void> {
  await apiFetch(path, options);
}
